import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  X, Plus, Briefcase, Users, Envelope, Trash, MagnifyingGlass, CheckCircle,
  Clock, MapPin, CurrencyDollar, Calendar, Sparkle,
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal that lets a contractor create a new INTERNAL project (skips the public job board)
 * and assigns workers manually — either from their existing pool (team members + previously
 * hired) or by inviting a new worker via email.
 *
 * Props:
 *  - open (bool)
 *  - onClose ()
 *  - onCreated(project)  — called after successful creation
 */
export default function NewInternalProjectModal({ open, onClose, onCreated }) {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = details, 2 = team
  const [submitting, setSubmitting] = useState(false);

  // Project details
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [budget, setBudget] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Workers
  const [eligibleWorkers, setEligibleWorkers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteList, setInviteList] = useState([]);

  useEffect(() => {
    if (!open) return;
    // Reset on each open
    setStep(1);
    setTitle(''); setDescription(''); setLocation(''); setBudget('');
    setStartDate(''); setEndDate('');
    setSelectedIds(new Set());
    setInviteList([]); setInviteEmail(''); setInviteName('');

    const token = localStorage.getItem('token');
    axios.get(`${API_URL}/api/internal-projects/eligible-workers`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => setEligibleWorkers(r.data?.workers || [])).catch(() => {});
  }, [open]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const addInvite = () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast({ title: 'Invalid email', variant: 'destructive' });
      return;
    }
    if (inviteList.find(i => i.email === email)) return;
    setInviteList([...inviteList, { email, name: inviteName.trim() }]);
    setInviteEmail(''); setInviteName('');
  };

  const removeInvite = (email) => {
    setInviteList(inviteList.filter(i => i.email !== email));
  };

  const filteredWorkers = eligibleWorkers.filter(w => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (w.name || '').toLowerCase().includes(q) ||
           (w.email || '').toLowerCase().includes(q) ||
           (w.trade || '').toLowerCase().includes(q);
  });

  const validStep1 = title.trim().length >= 2;

  const handleSubmit = async () => {
    if (!validStep1) return;
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      const payload = {
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        budget: budget ? Number(budget) : 0,
        start_date: startDate || null,
        end_date: endDate || null,
        worker_ids: Array.from(selectedIds),
        invite_emails: inviteList,
      };
      const res = await axios.post(`${API_URL}/api/internal-projects`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.success) {
        const inviteCount = (res.data.invites || []).filter(i => i.status === 'invited').length;
        toast({
          title: 'Project created',
          description: `${res.data.worker_count} worker(s) notified${inviteCount ? `, ${inviteCount} email invite(s) sent` : ''}.`,
        });
        onCreated && onCreated(res.data.project);
        onClose && onClose();
      }
    } catch (err) {
      toast({
        title: 'Could not create project',
        description: err.response?.data?.detail || 'Something went wrong',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="new-internal-project-modal"
    >
      <div
        className="bg-white dark:bg-neutral-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto my-8 border-2 border-black shadow-[6px_6px_0px_#3B82F6]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b-2 border-black px-6 py-4 bg-neutral-900 text-white rounded-t-xl">
          <h2 className="font-black uppercase tracking-wide flex items-center gap-2 text-base sm:text-lg">
            <Briefcase size={20} weight="fill" className="text-[#475569]" />
            New Internal Project
            <span className="text-[10px] font-bold bg-white/20 px-2 py-0.5 rounded-full uppercase ml-1">
              Step {step}/2
            </span>
          </h2>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="close-new-project-btn">
            <X size={22} weight="bold" />
          </button>
        </div>

        <div className="p-6">
          {/* STEP 1 — DETAILS */}
          {step === 1 && (
            <div className="space-y-4" data-testid="new-project-step-1">
              <div>
                <label className="block text-xs font-black uppercase text-neutral-500 mb-1">Project Title *</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  placeholder="e.g. Smith Residence Kitchen Remodel"
                  className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  data-testid="project-title-input"
                />
              </div>
              <div>
                <label className="block text-xs font-black uppercase text-neutral-500 mb-1">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                  rows={3}
                  placeholder="Scope, materials, special instructions..."
                  className="w-full px-4 py-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white resize-none"
                  data-testid="project-description-input"
                />
              </div>
              <div>
                <label className="block text-xs font-black uppercase text-neutral-500 mb-1 flex items-center gap-1">
                  <MapPin size={12} weight="bold" /> Location
                </label>
                <input
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="123 Oak St, Austin TX"
                  className="w-full h-12 px-4 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                  data-testid="project-location-input"
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-black uppercase text-neutral-500 mb-1 flex items-center gap-1">
                    <CurrencyDollar size={12} weight="bold" /> Budget
                  </label>
                  <input
                    type="number" value={budget} onChange={(e) => setBudget(e.target.value)}
                    placeholder="35000"
                    className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                    data-testid="project-budget-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase text-neutral-500 mb-1 flex items-center gap-1">
                    <Calendar size={12} weight="bold" /> Start
                  </label>
                  <input
                    type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                    data-testid="project-start-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black uppercase text-neutral-500 mb-1 flex items-center gap-1">
                    <Calendar size={12} weight="bold" /> End
                  </label>
                  <input
                    type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="w-full h-11 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                    data-testid="project-end-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — TEAM */}
          {step === 2 && (
            <div className="space-y-5" data-testid="new-project-step-2">
              <div>
                <h3 className="font-black text-sm text-neutral-900 dark:text-white mb-2 flex items-center gap-2">
                  <Users size={16} weight="bold" className="text-[#2B4C6F]" /> Pick from your team
                  <span className="text-[10px] font-normal text-neutral-500">({selectedIds.size} selected)</span>
                </h3>
                <div className="relative mb-2">
                  <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                  <input
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, trade, or email..."
                    className="w-full h-10 pl-9 pr-3 text-sm bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                    data-testid="worker-search-input"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto border-2 border-neutral-200 dark:border-neutral-700 rounded-lg">
                  {filteredWorkers.length === 0 ? (
                    <div className="p-6 text-center text-xs text-neutral-500">
                      No matching workers.<br/>
                      <span className="text-[11px]">Use the email invite below to add someone new.</span>
                    </div>
                  ) : (
                    filteredWorkers.map(w => (
                      <button
                        key={w.id}
                        type="button"
                        onClick={() => toggleSelect(w.id)}
                        className={`w-full flex items-center gap-3 p-3 border-b last:border-b-0 border-neutral-100 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors ${
                          selectedIds.has(w.id) ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                        }`}
                        data-testid={`worker-row-${w.id}`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedIds.has(w.id) ? 'bg-emerald-500 border-emerald-500' : 'border-neutral-300'
                        }`}>
                          {selectedIds.has(w.id) && <CheckCircle size={14} weight="bold" className="text-white" />}
                        </div>
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2B4C6F] to-[#3F6886] text-white font-bold text-xs flex items-center justify-center flex-shrink-0">
                          {(w.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <p className="font-bold text-sm text-neutral-900 dark:text-white truncate">{w.name || 'Worker'}</p>
                          <p className="text-[11px] text-neutral-500 truncate">
                            {w.trade ? `${w.trade} · ` : ''}{w.email}
                          </p>
                        </div>
                        <span className="text-[9px] uppercase font-black px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300">
                          {w.source === 'team' ? 'Team' : w.source === 'previously_hired' ? 'Hired' : 'Past'}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>

              <div className="border-t-2 border-dashed border-neutral-200 dark:border-neutral-700 pt-5">
                <h3 className="font-black text-sm text-neutral-900 dark:text-white mb-2 flex items-center gap-2">
                  <Envelope size={16} weight="bold" className="text-[#475569]" /> Invite new workers by email
                </h3>
                <div className="flex flex-col sm:flex-row gap-2 mb-2">
                  <input
                    value={inviteName} onChange={(e) => setInviteName(e.target.value)}
                    placeholder="Name (optional)"
                    className="sm:w-32 h-10 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                    data-testid="invite-name-input"
                  />
                  <input
                    value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="worker@example.com"
                    type="email"
                    className="flex-1 h-10 px-3 text-sm bg-white dark:bg-neutral-900 border-2 border-neutral-200 dark:border-neutral-600 rounded-lg text-neutral-900 dark:text-white"
                    data-testid="invite-email-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addInvite(); } }}
                  />
                  <button
                    type="button"
                    onClick={addInvite}
                    className="h-10 px-4 bg-[#3B82F6] hover:bg-[#1E40AF] text-[#0F172A] font-bold text-sm rounded-lg border-2 border-black transition-all flex items-center gap-1"
                    data-testid="add-invite-btn"
                  >
                    <Plus size={14} weight="bold" /> Add
                  </button>
                </div>
                {inviteList.length > 0 && (
                  <div className="space-y-1">
                    {inviteList.map(i => (
                      <div
                        key={i.email}
                        className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-xs"
                        data-testid={`invite-pill-${i.email}`}
                      >
                        <Sparkle size={12} weight="fill" className="text-amber-600" />
                        <span className="font-bold text-neutral-900 dark:text-white">{i.name || i.email}</span>
                        {i.name && <span className="text-neutral-500">· {i.email}</span>}
                        <button onClick={() => removeInvite(i.email)} className="ml-auto text-red-500 hover:text-red-700">
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t-2 border-neutral-200 dark:border-neutral-700 px-6 py-4 gap-3">
          {step === 1 ? (
            <>
              <button onClick={onClose} className="text-sm font-bold text-neutral-500 hover:text-neutral-900" data-testid="cancel-project-btn">
                Cancel
              </button>
              <button
                onClick={() => setStep(2)} disabled={!validStep1}
                className="h-11 px-5 bg-[#2563EB] hover:bg-[#4A7BA1] text-white font-bold text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-40"
                data-testid="next-step-btn"
              >
                Continue → Add Workers
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setStep(1)} className="text-sm font-bold text-neutral-500 hover:text-neutral-900" data-testid="back-step-btn">
                ← Back
              </button>
              <button
                onClick={handleSubmit} disabled={submitting}
                className="h-11 px-6 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-40 flex items-center gap-2"
                data-testid="create-project-btn"
              >
                {submitting ? <Clock size={16} weight="bold" className="animate-spin" /> : <CheckCircle size={16} weight="bold" />}
                {submitting ? 'Creating...' : `Create Project${selectedIds.size + inviteList.length > 0 ? ` & Notify ${selectedIds.size + inviteList.length}` : ''}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
